/**
 * Navigation Linking Configuration
 * Maps URLs to screen names for web navigation
 */

import { LinkingOptions } from '@react-navigation/native';

export type RootStackParamList = {
  // Auth screens
  splashScreen: undefined;
  welcomeScreen: undefined;
  LoginScreen: undefined;
  SignUpScreen: undefined;
  TermsAndConditionsScreen: undefined;
  OtpVerification: { email?: string; phone?: string };
  ProfileImgUpload: undefined;
  ForgotPasswordScreen: undefined;
  ResetPasswordScreen: { token?: string };
  ChangePasswordScreen: undefined;

  // Main screens
  Dashboard: undefined;
  ExploreScreen: undefined;
  NotificationScreen: undefined;

  // Live streaming
  LiveScreen: undefined;
  GoLiveScreen: undefined;
  StreamingLiveScreen: { streamId?: string; isHost?: boolean };
  MyLiveStreamingScreen: undefined;
  WowzaLiveStreamPlayer: { streamUrl?: string };
  PlayerScreen: { videoUrl?: string };
  LiveVideoStreamer: { streamKey?: string };

  // Chat
  ChatScreen: undefined;
  ChatUserScreen: { recipientId?: string; recipientName?: string };

  // Profile
  ProfileScreen: { userId?: string };
  EditProfileScreen: undefined;
  ViewOtherProfileScreen: { userId: string };
  FollowersScreen: { userId?: string };
  FollowingScreen: { userId?: string };
  PrivacyPolicyScreen: undefined;

  // Wallet & Payments
  CoinWalletScreen: undefined;
  AddCoinScreen: { cryptoPaymentResult?: { status: string; newBalance?: string; coins?: string } } | undefined;
  RedeemScreen: undefined;
  AddCardScreen: undefined;
  SubscriptionScreen: undefined;
  RedemptionHistoryScreen: undefined;

  // Platform accounts
  PlatformAccountsScreen: undefined;
  AddPlatformAccountScreen: undefined;
  PlatformCreditCoinScreen: undefined;

  // KYC
  KycWebViewScreen: { url?: string };

  // Crypto Payment
  CryptoPaymentWebViewScreen: {
    invoiceUrl?: string;
    orderId?: string;
    purchaseId?: number;
    coins?: string;
    amount?: string;
  };
};

export const linking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    'https://acestreamz.com',
    'https://www.acestreamz.com',
    'acestreamz://',
    // Add localhost for development
    'http://localhost:3000',
  ],
  config: {
    screens: {
      // Auth flow
      splashScreen: '',
      welcomeScreen: 'welcome',
      LoginScreen: 'login',
      SignUpScreen: 'signup',
      TermsAndConditionsScreen: 'terms',
      OtpVerification: 'verify-otp',
      ProfileImgUpload: 'profile-setup',
      ForgotPasswordScreen: 'forgot-password',
      ResetPasswordScreen: 'reset-password/:token?',
      ChangePasswordScreen: 'change-password',

      // Main screens
      Dashboard: 'dashboard',
      ExploreScreen: 'explore',
      NotificationScreen: 'notifications',

      // Live streaming
      LiveScreen: 'live',
      GoLiveScreen: 'go-live',
      StreamingLiveScreen: 'stream/:streamId?',
      MyLiveStreamingScreen: 'my-streams',
      WowzaLiveStreamPlayer: 'player/wowza',
      PlayerScreen: 'player',
      LiveVideoStreamer: 'broadcast',

      // Chat
      ChatScreen: 'messages',
      ChatUserScreen: 'chat/:recipientId?',

      // Profile
      ProfileScreen: 'profile/:userId?',
      EditProfileScreen: 'profile/edit',
      ViewOtherProfileScreen: 'user/:userId',
      FollowersScreen: 'followers/:userId?',
      FollowingScreen: 'following/:userId?',
      PrivacyPolicyScreen: 'privacy-policy',

      // Wallet & Payments
      CoinWalletScreen: 'wallet',
      AddCoinScreen: 'wallet/add-coins',
      RedeemScreen: 'wallet/redeem',
      AddCardScreen: 'wallet/add-card',
      SubscriptionScreen: 'subscription',
      RedemptionHistoryScreen: 'wallet/history',

      // Platform accounts
      PlatformAccountsScreen: 'platform-accounts',
      AddPlatformAccountScreen: 'platform-accounts/add',
      PlatformCreditCoinScreen: 'platform-credits',

      // KYC
      KycWebViewScreen: 'kyc',
      CryptoPaymentWebViewScreen: 'wallet/crypto-payment',
    },
  },
};

export default linking;
